import { Pressable, Platform, PixelRatio } from "react-native";
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useUnistyles } from "react-native-unistyles";
import { useRouter } from "expo-router";
import { useMemo } from "react";
import Unicon from "@/components/General/Unicon";
import { renderPageHeaderBackground } from "@/components/General/PageHeaderBackground";
import usePlatform from "./usePlatform";

// iOS 26+ uses the system "Liquid Glass" material for navigation headers by
// default — platform constant, no need to re-evaluate per render.
const isIOS26Plus = Platform.OS === 'ios' && parseInt(Platform.Version, 10) >= 26;

const useScreenOptions = () => {
    const { theme: { colors, fonts, scheme } } = useUnistyles()
    const { isWeb, isLandscape, isNative, isTablet } = usePlatform();
    const router = useRouter();
    const insets = useSafeAreaInsets();

    // The shared layout re-renders on every navigation (the plan gate reads
    // pathname); without a stable reference, each render would re-declare
    // ~120 fresh screen option objects. Memoized on the real inputs — theme,
    // platform flags, insets — so navigation alone returns the same object.
    return useMemo(() => {
        // We set headerTransparent so the header background is transparent (letting the glass
        // material show through) and avoid overriding headerStyle/headerShadowVisible/headerBlurEffect
        // so the system manages the material. contentStyle.paddingTop pushes content below the
        // transparent header so non-scrolling screens aren't hidden behind the glass.
        // On older iOS / Android / web we use a solid header background.
        const useTransparentHeader = isIOS26Plus && !isTablet;
        // Liquid glass nav bars are taller than the legacy 44pt bar. Heights mirror
        // react-navigation's getDefaultHeaderHeight: 60pt on Dynamic Island phones,
        // 64pt otherwise, plus a status bar that sits ~5px below the top inset on
        // Dynamic Island models.
        const hasDynamicIsland = insets.top > 50;
        const ios26HeaderHeight = (hasDynamicIsland ? 60 : 64)
            + Math.max(insets.top - (hasDynamicIsland ? 5 + 1 / PixelRatio.get() : 0), 0);

        return {
            headerShown: true,
            headerTintColor: colors.text,
            headerShadowVisible: useTransparentHeader ? undefined : false,
            headerTitleAlign: isWeb ? 'left' : 'center',
            headerTransparent: useTransparentHeader ? true : undefined,
            headerBackground: useTransparentHeader ? undefined : renderPageHeaderBackground,
            headerLeft: isWeb ? () => (
                <Pressable onPress={() => router.back()} hitSlop={10} style={{ marginRight: 8 }}>
                    <Unicon name="arrow-back" size={22} colorVariant="text" />
                </Pressable>
            ) : undefined,
            headerBackButtonDisplayMode: "minimal",
            headerLeftContainerStyle: {
                paddingLeft: (isWeb || isLandscape) ? 10 : 20
            },
            headerRightContainerStyle: {
                paddingRight: isWeb ? 20 : 10
            },
            headerTitleStyle: {
                textAlign: isWeb ? 'left' : 'center',
                fontSize: 18,
                fontFamily: fonts.bold,
            },
            headerTitleContainerStyle: {
                width: isWeb ? '100%' : '70%',
                alignItems: isWeb ? 'flex-start' : 'center',
            },
            headerStyle: useTransparentHeader ? undefined : {
                height: isNative ? 105 : 70,
                // The live background paints the color; a cached native shell must not cover it.
                backgroundColor: 'transparent',
                borderBottomWidth: isWeb ? 0 : 1,
                borderBottomColor: colors.border
            },
            headerBlurEffect: useTransparentHeader ? undefined : (isNative ? scheme : undefined),
            contentStyle: useTransparentHeader ? {
                paddingTop: ios26HeaderHeight,
            } : undefined,
        }
    }, [colors.text, colors.border, fonts.bold, scheme, isWeb, isLandscape, isNative, isTablet, insets.top, router]);
}

export default useScreenOptions
